// Northern Illinois University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - NIU posts no Common Data Set: its Institutional Research page links the
//    IBHE data tool and NCES College Navigator instead, and a search of the
//    Institutional Effectiveness pages and the niu.edu sitemaps found none.
//    The first-year counts come from the U.S. Department of Education's
//    IPEDS Admissions survey for Fall 2024, first-time degree-seeking
//    students (College Navigator and the ADM2024 complete data file, unit
//    147703). IPEDS gives no residency split, so this page has none.
//  - niu.edu Undergraduate Admissions: Freshman (First-year) Student
//    Application, Test-free Admissions Processes, and Application Deadlines.
//    The deadlines table carries no year; it is the page as posted for the
//    application cycle open now (applications for fall open August 1).
//  - The School of Music, School of Nursing and School of Theatre and Dance
//    admission pages, for the programs with added requirements.
//  - NIU Financial Aid and Scholarship Office, What Are My Costs?: the
//    2026-2027 undergraduate full-time cost of attendance. It gives one
//    budget, with no separate out-of-state figure.
//  - College Scorecard API, school id 147703 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=147703#admsns',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'NIU Undergraduate Admissions, Freshman (First-year) Student Application',
        url: 'https://www.niu.edu/admissions/path/first-year/index.shtml',
        read: '2026-10-01',
    },
    testFree: {
        label: 'NIU Undergraduate Admissions, Test-free Admissions Processes',
        url: 'https://www.niu.edu/admissions/path/first-year/test-free.shtml',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'NIU Undergraduate Admissions, Application Deadlines',
        url: 'https://www.niu.edu/admissions/apply/deadlines/index.shtml',
        read: '2026-10-01',
    },
    music: {
        label: 'NIU School of Music, Undergraduate Admissions',
        url: 'https://www.niu.edu/music/admissions/undergraduate.shtml',
        read: '2026-10-01',
    },
    nursing: {
        label: 'NIU School of Nursing, Bachelor of Science in Nursing',
        url: 'https://www.chhs.niu.edu/nursing/bs/index.shtml',
        read: '2026-10-01',
    },
    theatre: {
        label: 'NIU School of Theatre and Dance, Undergraduate Admissions',
        url: 'https://www.niu.edu/theatre/academics/undergraduate/admissions/index.shtml',
        read: '2026-10-01',
    },
    coa: {
        label: 'NIU Financial Aid and Scholarship Office, What Are My Costs?',
        url: 'https://www.niu.edu/financial-aid/process/costs.shtml',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?147703-Northern-Illinois-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'northern-illinois-university',
    kbId: 'northern_illinois_university',
    name: 'Northern Illinois University',
    shortName: 'NIU',
    aliases: ['Northern Illinois'],
    location: 'DeKalb, IL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (NIU communication standards, brand colors); decoration only.
    branding: {
        primary: '#C8102E',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/northern_illinois_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 23651,
        admitted: 16505,
        enrolled: 1991,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'NIU does not post a Common Data Set, so these counts come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole university and none by major.',
        source: 'ipeds',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Three schools add requirements to the general application:',
        rows: [
            { school: 'School of Music', requirement: 'An audition. Formal audition days are held in February, and each application step must be done by your audition date.', source: 'music' },
            { school: 'School of Nursing', requirement: 'A competitive program with its own application, judged on a full admission profile that includes grades in prerequisite courses; you need a prerequisite GPA and an overall GPA of at least 2.75. For a fall program start, the application opens November 1 and closes January 15.', source: 'nursing' },
            { school: 'School of Theatre and Dance', requirement: 'An audition or interview for the B.F.A. in acting or design and technology. You must have applied to NIU by then, though you need not be admitted yet.', source: 'theatre' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'General deadline', value: 'August 1' },
        rows: [
            { plan: 'Merit scholarship priority deadline', deadline: 'February 1', decision: null, binding: false },
            { plan: 'Review deadline (GPA below 3.0)', deadline: 'July 15', decision: null, binding: false },
            { plan: 'General deadline', deadline: 'August 1', decision: 'Usually two to four weeks after your file is complete', binding: false },
        ],
        note: 'NIU publishes no early decision or early action plan: most applicants can apply any time between the opening date (August 1 for fall) and the general deadline. Apply by February 1 to be considered for merit scholarships; awards continue after that while funds last. Freshmen with a GPA below 3.0 should apply by the July 15 review deadline, because their applications get a holistic review. Admitted students confirm by August 15. A deadline that falls on a weekend moves to the next Monday.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'NIU is test-free: SAT and ACT scores are not considered for general admission or merit scholarships, even if you send them. A cumulative high school GPA of 3.0 or higher guarantees general admission, though some limited-admission programs add requirements; below 3.0, your application gets a holistic review. A few other scholarships still require the SAT or ACT, and NCAA Division I athletes need a score for eligibility.',
        source: 'testFree',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 11010 },
            { item: 'Fees', amount: 3390 },
            { item: 'Loan fees', amount: 74 },
            { item: 'Living expenses (on campus)', amount: 15046 },
            { item: 'Transportation', amount: 2160 },
            { item: 'Books, supplies, course materials and equipment', amount: 632 },
            { item: 'Miscellaneous personal expenses', amount: 1560 },
        ],
        total: 33872,
        note: 'For a full-time undergraduate (12 or more hours a semester) living on campus; living expenses are the average on-campus housing allowance with the Huskie Block 19 meal plan. Tuition is higher for engineering ($12,210), computer science ($11,970) and nursing ($11,370) majors. Fees leave out the student health insurance plan. The aid office gives one budget, with no separate out-of-state figure.',
        source: 'coa',
        netPrice: {
            average: 14505,
            byIncome: [
                { income: '$0 to $30,000', amount: 12319 },
                { income: '$30,001 to $48,000', amount: 11502 },
                { income: '$48,001 to $75,000', amount: 13387 },
                { income: '$75,001 to $110,000', amount: 20105 },
                { income: 'Over $110,000', amount: 24295 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from NIU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
